import React from 'react';
import i18n from 'util/i18n';
import ValidationElement from 'components/Form/ValidationElement';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import Field from 'components/Form/Field';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import NotApplicable from 'components/Form/NotApplicable';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import { FinancialDelinquentItemModel } from '../../../../forms/sections/financial/delinquent/types';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from '../../../../types/props';
import { ValidationElementProps } from '../../../Form/ValidationElement/ValidationElement';
import Infractions from './Infractions';
import { isPVQForm } from 'helpers/form';
import Branch from 'components/Form/Branch';

interface Props extends Partial<FinancialDelinquentItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks

  attribute?: string
  years: number
  yearsString: string
  requireFinancialDelinquentName?: boolean
  allowFinancialDelinquentNonFederal?: boolean
  formType?: string
  dispatch?: Dispatch
}

export default class DelinquentItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
  }

  update = (queue: Partial<FinancialDelinquentItemModel>) => {
    const { allowFinancialDelinquentNonFederal } = this.props;
    const infractionValues = allowFinancialDelinquentNonFederal
      ? this.props.Infractions
      : ['Federal'];

    this.props.onUpdate({
      Name: this.props.Name,
      Infractions: infractionValues,
      AccountNumber: this.props.AccountNumber,
      PropertyType: this.props.PropertyType,
      Amount: this.props.Amount,
      AmountEstimated: this.props.AmountEstimated,
      Reason: this.props.Reason,
      Status: this.props.Status,
      Date: this.props.Date,
      ResolvedNotApplicable: this.props.ResolvedNotApplicable,
      Resolved: this.props.Resolved,
      CourtName: this.props.CourtName,
      CourtAddress: this.props.CourtAddress,
      Description: this.props.Description,
      AccountNumberNotApplicable: this.props.AccountNumberNotApplicable,
      CourtNameNotApplicable: this.props.CourtNameNotApplicable,
      CourtAddressNotApplicable: this.props.CourtAddressNotApplicable,
      AccountNumberExplanation: this.props.AccountNumberExplanation,
      CourtNameExplanation: this.props.CourtNameExplanation,
      CourtAddressExplanation: this.props.CourtAddressExplanation,
      DebtExplanation: this.props.DebtExplanation,
      HasActionTaken: this.props.HasActionTaken,
      ActionsTaken: this.props.ActionsTaken,
      NoActionsTaken: this.props.NoActionsTaken,
      ActionsTakenNoneExplanation: this.props.ActionsTakenNoneExplanation,
      ...queue,
    });
  };

  updateName = (values: FinancialDelinquentItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateInfractions = (values: FinancialDelinquentItemModel['Infractions']) => {
    this.update({
      Infractions: values,
    });
  };

  updateAccountNumber = (values: FinancialDelinquentItemModel['AccountNumber']) => {
    this.update({
      AccountNumber: values,
    });
  };

  updateAccountNumberNotApplicable = (value: FinancialDelinquentItemModel['AccountNumberNotApplicable']) => {
    this.update({
      AccountNumber: value.applicable ? this.props.AccountNumber : {},
      AccountNumberNotApplicable: value,
      AccountNumberExplanation: value.applicable ? {} : this.props.AccountNumberExplanation,
    });
  };

  updatePropertyType = (values: FinancialDelinquentItemModel['PropertyType']) => {
    this.update({
      PropertyType: values,
    });
  };

  updateAmount = (values: FinancialDelinquentItemModel['Amount']) => {
    this.update({
      Amount: values,
    });
  };

  updateAmountEstimated = (values: FinancialDelinquentItemModel['AmountEstimated']) => {
    this.update({
      AmountEstimated: values,
    });
  };

  updateReason = (values: FinancialDelinquentItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateStatus = (values: FinancialDelinquentItemModel['Status']) => {
    this.update({
      Status: values,
    });
  };

  updateDate = (values: FinancialDelinquentItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateResolved = (values: FinancialDelinquentItemModel['Resolved']) => {
    this.update({
      Resolved: values,
    });
  };

  updateResolvedNotApplicable = (values: FinancialDelinquentItemModel['ResolvedNotApplicable']) => {
    this.update({
      Resolved: values.applicable ? this.props.Resolved : {},
      ResolvedNotApplicable: values,
    });
  };

  updateCourtName = (values: FinancialDelinquentItemModel['CourtName']) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtNameNotApplicable = (value: FinancialDelinquentItemModel['CourtNameNotApplicable']) => {
    this.update({
      CourtName: value.applicable ? this.props.CourtName : {},
      CourtNameNotApplicable: value,
      CourtNameExplanation: value.applicable ? {} : this.props.CourtNameExplanation,
    });
  };

  updateCourtAddress = (values: FinancialDelinquentItemModel['CourtAddress']) => {
    this.update({
      CourtAddress: values,
    });
  };

  updateCourtAddressNotApplicable = (value: FinancialDelinquentItemModel['CourtAddressNotApplicable']) => {
    this.update({
      CourtAddress: value.applicable ? this.props.CourtAddress : {},
      CourtAddressNotApplicable: value,
      CourtAddressExplanation: value.applicable ? {} : this.props.CourtAddressExplanation,
    });
  };

  updateDescription = (values: FinancialDelinquentItemModel['Description']) => {
    this.update({
      Description: values,
    });
  };

  updateAccountNumberExplanation = (values: FinancialDelinquentItemModel['AccountNumberExplanation']) => {
    this.update({
      AccountNumberExplanation: values,
    });
  };

  updateCourtNameExplanation = (values: FinancialDelinquentItemModel['CourtNameExplanation']) => {
    this.update({
      CourtNameExplanation: values,
    });
  };

  updateCourtAddressExplanation = (values: FinancialDelinquentItemModel['CourtAddressExplanation']) => {
    this.update({
      CourtAddressExplanation: values,
    });
  };

  updateDebtExplanation = (value: FinancialDelinquentItemModel['DebtExplanation']) => {
    this.update({
      DebtExplanation: value,
    });
  };
  
  updateHasActionTaken = (value: FinancialDelinquentItemModel['HasActionTaken']) => {
    this.update({
      HasActionTaken: value,
      Resolved: value.value === 'No' ? this.props.Resolved : {},
      ActionsTaken: value.value === 'Yes' ? this.props.ActionsTaken : {},
      NoActionsTaken: value.value === 'Yes' ? this.props.NoActionsTaken : { applicable: true },
      ActionsTakenNoneExplanation: value.value === 'Yes' ? this.props.ActionsTakenNoneExplanation : { applicable: true },

    });
  };

  updateActionsTaken = (value: FinancialDelinquentItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: value,
    });
  };

  updateNoActionsTaken = (value: FinancialDelinquentItemModel['NoActionsTaken']) => {
    this.update({
      NoActionsTaken: value,
      ActionsTaken: value.applicable ? this.props.ActionsTaken : {},
      ActionsTakenNoneExplanation: value.applicable ? {} : this.props.ActionsTakenNoneExplanation,
    });
  };

  updateActionsTakenNoneExplanation = (value: FinancialDelinquentItemModel['ActionsTakenNoneExplanation']) => {
    this.update({
      ActionsTakenNoneExplanation: value,
    });
  };

  render() {
    const {
      years,
      yearsString,
      requireFinancialDelinquentName,
      allowFinancialDelinquentNonFederal,
    } = this.props;

    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;

    return isPVQForm(this.props.formType) ? (
      <>
        <div className="delinquent-item" />
        <Field
          title={i18n.t('financial.pvq.pastDueDebt.ifYes.debtNameText')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DebtExplanation`}
        >
          <Textarea
            name="DebtExplanation"
            className="DebtExplanation"
            {...this.props.DebtExplanation}
            onUpdate={this.updateDebtExplanation}
            onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
            required={this.props.required}
            attribute={`${attribute}.DebtExplanation`} />
        </Field>

        <Field
          title={i18n.t('financial.pvq.pastDueDebt.ifYes.whyTextArea')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            name="reason"
            className="reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
            required={this.props.required}
            attribute={`${attribute}.Reason`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.pastDueDebt.ifYes.amountTextEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Amount`}
        >
          <Currency
            {...this.props.Amount}
            attribute={`${attribute}.Amount`}
            name="amount"
            onUpdate={this.updateAmount}
            onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
          <Checkbox
            attribute={`${attribute}.AmountEstimated`}
            name="AmountEstimated"
            className="card-estimated"
            {...this.props.AmountEstimated}
            label={i18n.t('financial.pvq.shared.estimated')}
            toggle="false"
            onUpdate={this.updateAmountEstimated}
            onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
          />
        </Field>
        <Field
          title={i18n.t('financial.pvq.pastDueDebt.ifYes.missedPayments.whenFirstDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            {...this.props.Date}
            attribute={`${attribute}.Date`}
            name="missed-payment-date"
            onUpdate={this.updateDate}
            hideDay
            onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated
          />
        </Field>
        <Branch
          name="has_actiontaken"
          label={i18n.t('financial.pvq.pastDueDebt.ifYes.branch.title')}
          labelSize="h4"
          className="hasactiontaken-branch"
          {...this.props.HasActionTaken}
          onUpdate={this.updateHasActionTaken}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
          attribute="HasActionTaken"
        />
        {this.props.HasActionTaken?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.pastDueDebt.ifYes.branch.ifNo.whenDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Resolved`}
          >
            <DateControl
              {...this.props.Resolved}
              attribute={`${attribute}.Resolved`}
              name="resolved"
              onUpdate={this.updateResolved}
              onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              hideDay
            />
          </Field>
        )}
        {this.props.HasActionTaken?.value === 'Yes' && (
          <>
            <Field
              title={i18n.t('financial.pvq.pastDueDebt.ifYes.branch.ifYes.combobox.actionsTaken')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ActionsTaken`}
            >
              <NotApplicable
                name="noactionstaken"
                label="None"
                {...this.props.NoActionsTaken}
                onUpdate={this.updateNoActionsTaken}
                onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
                attribute={`${this.props.attribute}.NoActionsTaken`}
              >
                <Textarea
                  {...this.props.ActionsTaken}
                  attribute={`${attribute}.ActionsTaken`}
                  onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
                  onUpdate={this.updateActionsTaken}
                  required={this.props.required}
                />
              </NotApplicable>
            </Field>
            {this.props.NoActionsTaken?.applicable === false && (
            <Field
              title={i18n.t('financial.pvq.pastDueDebt.ifYes.branch.ifYes.combobox.ifNone')}
              titleSize="h4"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ActionsTakenNoneExplanation`}
            >
              <Textarea
                {...this.props.ActionsTakenNoneExplanation}
                attribute={`${attribute}.ActionsTakenNoneExplanation`}
                name="action-taken-explanation"
                onUpdate={this.updateActionsTakenNoneExplanation}
                onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView} />
            </Field>
            )}
          </>
        )}

        <Field
          title={i18n.t('financial.pvq.pastDueDebt.ifYes.amountCurrencyIdk')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AccountNumber`}
        >
          <NotApplicable
            name="AccountNumberNotApplicable"
            className="na-accountnumber"
            label={i18n.t('financial.delinquent.label.idk')}
            {...this.props.AccountNumberNotApplicable}
            onUpdate={this.updateAccountNumberNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.AccountNumberNotApplicable`}
            includeNAConfirmation={!!(this.props.AccountNumber?.value || this.props.AccountNumberExplanation?.value)}
          >
            <Text
              {...this.props.AccountNumber}
              attribute={`${attribute}.AccountNumber`}
              name="AccountNumber"
              onUpdate={this.updateAccountNumber}
              onError={this.props.onError} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

      </>

    ) : (
      <div className="delinquent-item">
        {requireFinancialDelinquentName && (
        <Field
          title={i18n.t('financial.delinquent.heading.name')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Name`}
        >
          <Text
            name="Name"
            {...this.props.Name}
            onUpdate={this.updateName}
            onError={this.props.onError}
            className="delinquent-name"
            required={this.props.required}
            attribute={`${attribute}.Name`}
          />
        </Field>
        )}

        {allowFinancialDelinquentNonFederal && (
        <Field
          title={i18n.t('financial.delinquent.heading.infractions')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Infractions`}
        >
          <Infractions
            name="Infractions"
            {...this.props.Infractions}
            onUpdate={this.updateInfractions}
            onError={this.props.onError}
            className="delinquent-infractions"
            required={this.props.required}
            years={years}
            yearsString={yearsString}
            formType={this.props.formType}
            attribute={`${attribute}.Infractions`}
          />
        </Field>
        )}

        <Field
          title={i18n.t('financial.delinquent.heading.accountnumber')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AccountNumber`}
          className="minimal-margin-bottom"
          exactAttribute
        >
          <NotApplicable
            name="AccountNumberNotApplicable"
            className="na-accountnumber"
            label={i18n.t('financial.delinquent.label.idk')}
            or={i18n.t('financial.delinquent.para.or')}
            {...this.props.AccountNumberNotApplicable}
            onUpdate={this.updateAccountNumberNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.AccountNumberNotApplicable`}
            includeNAConfirmation={Boolean(this.props.AccountNumber?.value || this.props.AccountNumberExplanation?.value)}
          >
            <Text
              name="AccountNumber"
              {...this.props.AccountNumber}
              onUpdate={this.updateAccountNumber}
              onError={this.props.onError}
              className="delinquent-accountnumber"
              required={this.props.required}
              attribute={`${attribute}.AccountNumber`}
            />
          </NotApplicable>
        </Field>
        {this.props.AccountNumberNotApplicable?.applicable === false && (
        <Field
          title={i18n.t('financial.delinquent.heading.accountNumberExplanation')}
          attribute={`${attribute}.AccountNumberExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="AccountNumberExplanation"
            {...this.props.AccountNumberExplanation}
            onUpdate={this.updateAccountNumberExplanation}
            onError={this.props.onError}
            className="explanation-comment"
            required={this.props.required}
            attribute={`${attribute}.AccountNumberExplanation`}
          />
        </Field>
        )}

        <Field
          title={i18n.t('financial.delinquent.heading.propertytype')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.PropertyType`}
        >
          <Text
            name="PropertyType"
            {...this.props.PropertyType}
            onUpdate={this.updatePropertyType}
            onError={this.props.onError}
            className="delinquent-propertytype"
            required={this.props.required}
            attribute={`${attribute}.PropertyType`}
          />
        </Field>

        <Field
          title={i18n.t('financial.delinquent.heading.amount')}
          className="delinquent-amount-notapplicable"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Amount`}
        >
          <div>
            <Currency
              name="Amount"
              {...this.props.Amount}
              onUpdate={this.updateAmount}
              onError={this.props.onError}
              className="delinquent-amount"
              min="1"
              required={this.props.required}
              attribute={`${attribute}.Amount`}
            />
            <div className="flags">
              <Checkbox
                name="AmountEstimated"
                label={i18n.t('financial.delinquent.label.estimated')}
                toggle="false"
                onUpdate={this.updateAmountEstimated}
                onError={this.props.onError}
                {...this.props.AmountEstimated}
                attribute={`${attribute}.AmountEstimated`}
              />
            </div>
          </div>
        </Field>

        <Field
          title={i18n.t('financial.delinquent.heading.reason')}
          scrollIntoView={this.props.scrollIntoView}
          help="financial.delinquent.help.reason"
          attribute={`${attribute}.Reason`}
        >
          <Textarea
            name="Reason"
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            className="delinquent-reason"
            required={this.props.required}
            attribute={`${attribute}.Reason`}
          />
        </Field>

        <Field
          title={i18n.t('financial.delinquent.heading.status')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Status`}
        >
          <Text
            name="Status"
            {...this.props.Status}
            onUpdate={this.updateStatus}
            onError={this.props.onError}
            className="delinquent-status"
            required={this.props.required}
            attribute={`${attribute}.Status`}
          />
        </Field>

        <Field
          title={i18n.t('financial.delinquent.heading.date')}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            onUpdate={this.updateDate}
            onError={this.props.onError}
            minDateEqualTo
            className="delinquent-date"
            hideDay
            required={this.props.required}
            attribute={`${attribute}.Date`}
          />
        </Field>

        <Field
          title={i18n.t('financial.delinquent.heading.resolved')}
          adjustFor="label"
          scrollIntoView={this.props.scrollIntoView}
          shrink
          attribute={`${attribute}.Resolved`}
        >
          <NotApplicable
            name="ResolvedNotApplicable"
            label={i18n.t('financial.delinquent.label.notresolved')}
            or={i18n.m('financial.delinquent.para.or')}
            {...this.props.ResolvedNotApplicable}
            onUpdate={this.updateResolvedNotApplicable}
            onError={this.props.onError}
            attribute={`${attribute}.ResolvedNotApplicable`}
            includeNAConfirmation={Boolean(this.props.Resolved?.month || this.props.Resolved?.year)}
          >
            <DateControl
              name="Resolved"
              {...this.props.Resolved}
              prefix="delinquent"
              onUpdate={this.updateResolved}
              onError={this.props.onError}
              minDate={this.props.Date}
              minDateEqualTo
              className="delinquent-resolved"
              hideDay
              required={this.props.required}
              attribute={`${attribute}.Resolved`}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.delinquent.heading.courtname')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
          className="minimal-margin-bottom"
          exactAttribute
        >
          <NotApplicable
            name="CourtNameNotApplicable"
            className="na-courtname"
            label={i18n.t('financial.delinquent.label.idk')}
            or={i18n.t('financial.delinquent.para.or')}
            {...this.props.CourtNameNotApplicable}
            onUpdate={this.updateCourtNameNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.CourtNameNotApplicable`}
            includeNAConfirmation={Boolean(this.props.CourtName?.value || this.props.CourtNameExplanation?.value)}
          >
            <Text
              name="CourtName"
              {...this.props.CourtName}
              onUpdate={this.updateCourtName}
              onError={this.props.onError}
              className="delinquent-courtname"
              required={this.props.required}
              attribute={`${attribute}.CourtName`}
            />
          </NotApplicable>
        </Field>
        {this.props.CourtNameNotApplicable?.applicable === false && (
        <Field
          title={i18n.t('financial.delinquent.heading.courtNameExplanation')}
          attribute={`${attribute}.CourtNameExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            title="CourtNameExplanation"
            className="explanation-comment"
            attribute={`${attribute}.CourtNameExplanation`}
            {...this.props.CourtNameExplanation}
            onUpdate={this.updateCourtNameExplanation}
            required={this.props.required}
            onError={this.props.onError}
          />
        </Field>
        )}

        <Field
          title={i18n.t('financial.delinquent.heading.courtaddress')}
          optional
          help="financial.delinquent.help.courtaddress"
          scrollIntoView={this.props.scrollIntoView}
          adjustFor="address"
          attribute={`${attribute}.CourtAddress`}
          className="minimal-margin-bottom"
          exactAttribute
        >
          <NotApplicable
            name="CourtAddressNotApplicable"
            className="na-courtaddress"
            label={i18n.t('financial.delinquent.label.idk')}
            or={i18n.t('financial.delinquent.para.or')}
            {...this.props.CourtAddressNotApplicable}
            onUpdate={this.updateCourtAddressNotApplicable}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.CourtAddressNotApplicable`}
            includeNAConfirmation={Boolean(this.props.CourtAddressExplanation?.value
              || (this.props.CourtAddress
                && (this.props.CourtAddress.city
                  || this.props.CourtAddress.zipcode
                  || this.props.CourtAddress.street
                  || this.props.CourtAddress.state
                  || (this.props.CourtAddress.country
                    && this.props.CourtAddress.country.value !== 'United States'
                    && this.props.CourtAddress.country.value !== 'INTERNATIONAL'
                  )
                )
              ))}
          >
            <Location
              name="CourtAddress"
              {...this.props.CourtAddress}
              onUpdate={this.updateCourtAddress}
              onError={this.props.onError}
              layout={Location.ADDRESS}
              geocode
              className="delinquent-courtaddress"
              showPostOffice
              dispatch={this.props.dispatch}
              addressBooks={this.props.addressBooks}
              addressBook="Court"
              required={this.props.required}
              attribute={`${attribute}.CourtAddress`}
            />
          </NotApplicable>
        </Field>
        {this.props.CourtAddressNotApplicable?.applicable === false && (
        <Field
          title={i18n.t('financial.delinquent.heading.courtAddressExplanation')}
          attribute={`${attribute}.CourtAddressExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            title="CourtAddressExplanation"
            className="explanation-comment"
            attribute={`${attribute}.CourtAddressExplanation`}
            {...this.props.CourtAddressExplanation}
            onUpdate={this.updateCourtAddressExplanation}
            required={this.props.required}
            onError={this.props.onError}
          />
        </Field>
        )}

        <Field
          title={i18n.t('financial.delinquent.heading.description')}
          scrollIntoView={this.props.scrollIntoView}
          help="financial.delinquent.help.description"
          attribute={`${attribute}.Description`}
        >
          <Textarea
            name="Description"
            {...this.props.Description}
            onUpdate={this.updateDescription}
            onError={this.props.onError}
            className="delinquent-description"
            required={this.props.required}
            attribute={`${attribute}.Description`}
          />
        </Field>
      </div>
    );
  }
}

DelinquentItem.defaultProps = {
  ResolvedNotApplicable: { applicable: true },
  NoActionsTaken: { applicable: true },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode) => arr,
  required: false,
};
